import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from "react";
import AuditTimeline, {
  type TAuditTimelineItem
} from "../components/audit-timeline";
import type {
  TEntityAuditRow,
  TEntityFeatureAudit,
  TEntityFeatureHistory
} from "../hooks/entity-audit-data";

type TEntityAuditMasterRow = {
  groupKey: string;
  groupTitle: string;
  records: TEntityAuditRow[];
  createdBy: string;
  createdAt: string | null;
  updatedBy: string;
  updatedAt: string | null;
};

const formatAuditDisplayValue = (
  fieldName: string | null,
  value: string | number | null
): string | number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== "string") {
    return value;
  }

  const normalizedField = (fieldName || "").toLowerCase();
  const isTimestampAuditField =
    normalizedField === "created_at" ||
    normalizedField === "updated_at" ||
    normalizedField.includes("date");
  if (!isTimestampAuditField) {
    return value;
  }

  const parsed = moment(value, moment.ISO_8601, true);
  if (!parsed.isValid()) {
    return value;
  }

  // Show a readable local timestamp for date-like audit fields.
  return parsed.format("MM-DD-YYYY hh:mm A");
};

function formatUserDisplay(
  user:
    | {
        first_name: string | null;
        last_name: string | null;
        email: string | null;
      }
    | null
    | undefined
): string {
  if (!user) {
    return "";
  }
  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  if (fullName && user.email) {
    return `${fullName} (${user.email})`;
  }
  if (fullName) {
    return fullName;
  }
  if (user.email) {
    return user.email;
  }
  return "";
}

function buildRecordedByLineForAudit(auditItem: TEntityFeatureAudit): string {
  const display = formatUserDisplay(auditItem.user_created);
  if (display) {
    return `By: ${display}`;
  }
  if (auditItem.created_by) {
    return `By: ${auditItem.created_by}`;
  }
  return "By: Unknown";
}

function buildRecordedByLineForHistory(
  historyItem: TEntityFeatureHistory
): string {
  const display = formatUserDisplay(
    historyItem.feature_instance_history_created_by_user
  );
  if (display) {
    return `By: ${display}`;
  }
  return "By: Unknown";
}

const buildTimelineItems = (
  records: TEntityAuditRow[]
): TAuditTimelineItem[] => {
  const mergedItems: TAuditTimelineItem[] = [];

  for (const element of records) {
    const featureInstance = element;
    const featureTitle = featureInstance?.title || "Untitled";
    const entityLabel = featureInstance?.entity_type_label || "Entity";
    const audits = featureInstance?.audits || [];
    const history = featureInstance?.history || [];

    for (let auditIndex = 0; auditIndex < audits.length; auditIndex += 1) {
      const auditItem = audits[auditIndex];
      const hasNewDate =
        auditItem?.new_date !== null && auditItem?.new_date !== undefined;
      const hasNewNumber =
        auditItem?.new_number !== null && auditItem?.new_number !== undefined;
      const hasNewString =
        auditItem?.new_string !== null && auditItem?.new_string !== undefined;

      let fromValue: string | number | null = null;
      let toValue: string | number | null = null;

      if (hasNewDate) {
        fromValue = auditItem?.old_date;
        toValue = auditItem?.new_date;
      } else if (hasNewNumber) {
        fromValue = auditItem?.old_number;
        toValue = auditItem?.new_number;
      } else if (hasNewString) {
        fromValue = auditItem?.old_string;
        toValue = auditItem?.new_string;
      }

      const formattedFromValue = formatAuditDisplayValue(
        auditItem?.field || null,
        fromValue
      );
      const formattedToValue = formatAuditDisplayValue(
        auditItem?.field || null,
        toValue
      );

      mergedItems.push({
        id: `audit-${featureInstance.id}-${auditIndex}`,
        timestamp: auditItem?.updated_at || auditItem?.created_at || null,
        label: auditItem?.event_log?.event?.name || "Field updated",
        entityLabel,
        title: featureTitle,
        description: `${auditItem?.field || "Field"}: ${formattedFromValue ?? "-"} -> ${formattedToValue ?? "-"}`,
        recordedByLine: buildRecordedByLineForAudit(auditItem)
      });
    }

    for (
      let historyIndex = 0;
      historyIndex < history.length;
      historyIndex += 1
    ) {
      const historyItem = history[historyIndex];
      const actionText =
        historyItem?.action_description || "No action description available.";
      mergedItems.push({
        id: `history-${featureInstance.id}-${historyIndex}`,
        timestamp: historyItem?.updated_at || historyItem?.created_at || null,
        label: "History",
        entityLabel,
        title: featureTitle,
        description: actionText,
        recordedByLine: buildRecordedByLineForHistory(historyItem)
      });
    }
  }

  return mergedItems.sort((firstItem, secondItem) => {
    const firstTime = firstItem.timestamp
      ? moment(firstItem.timestamp).valueOf()
      : 0;
    const secondTime = secondItem.timestamp
      ? moment(secondItem.timestamp).valueOf()
      : 0;
    return secondTime - firstTime;
  });
};

const getTimeValue = (
  value: string | null | undefined,
  fallback: number
): number => (value ? moment(value).valueOf() : fallback);

const formatRecordUserName = (
  user:
    | { first_name: string | null; last_name: string | null }
    | null
    | undefined
): string =>
  user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : "";

const groupRecordsByTitle = (
  filteredRows: TEntityAuditRow[]
): TEntityAuditMasterRow[] => {
  const groupedRows: Record<string, TEntityAuditMasterRow> = {};

  for (const item of filteredRows) {
    const groupTitle = item?.title || "Untitled";
    const groupKey = `title:${groupTitle}`;

    if (!groupedRows[groupKey]) {
      groupedRows[groupKey] = {
        groupKey,
        groupTitle,
        records: [],
        createdBy: "",
        createdAt: null,
        updatedBy: "",
        updatedAt: null
      };
    }

    groupedRows[groupKey].records.push(item);
  }

  return Object.values(groupedRows);
};

const findBoundaryRecords = (
  records: TEntityAuditRow[]
): {
  earliestCreatedRecord: TEntityAuditRow;
  latestUpdatedRecord: TEntityAuditRow;
} => {
  let earliestCreatedRecord = records[0];
  let latestUpdatedRecord = records[0];

  for (let recordIndex = 1; recordIndex < records.length; recordIndex += 1) {
    const currentRecord = records[recordIndex];

    const isEarlierCreated =
      getTimeValue(currentRecord?.created_at, Number.MAX_SAFE_INTEGER) <
      getTimeValue(earliestCreatedRecord?.created_at, Number.MAX_SAFE_INTEGER);
    if (isEarlierCreated) {
      earliestCreatedRecord = currentRecord;
    }

    const isLaterUpdated =
      getTimeValue(currentRecord?.updated_at, 0) >
      getTimeValue(latestUpdatedRecord?.updated_at, 0);
    if (isLaterUpdated) {
      latestUpdatedRecord = currentRecord;
    }
  }

  return { earliestCreatedRecord, latestUpdatedRecord };
};

const applyAggregatesToRow = (row: TEntityAuditMasterRow): void => {
  // groupRecordsByTitle always initializes records:[] then pushes >=1 item per
  // group, so a master row's `records` is never falsy/empty here — dead guard.
  /* v8 ignore start */
  const records = row.records || [];
  if (records.length === 0) {
    return;
  }
  /* v8 ignore stop */

  const { earliestCreatedRecord, latestUpdatedRecord } =
    findBoundaryRecords(records);

  row.createdBy = formatRecordUserName(earliestCreatedRecord?.created_by_user);
  row.createdAt = earliestCreatedRecord?.created_at || null;
  row.updatedBy = formatRecordUserName(latestUpdatedRecord?.updated_by_user);
  row.updatedAt = latestUpdatedRecord?.updated_at || null;
};

function EntityTimelineDetailRenderer(
  props: Readonly<{ data?: TEntityAuditMasterRow }>
) {
  const timelineItems = useMemo(
    () => buildTimelineItems(props?.data?.records || []),
    [props?.data?.records]
  );

  return <AuditTimeline items={timelineItems} />;
}

export type TFeatureInstanceAuditGridProps = {
  loading: boolean;
  filteredRows: TEntityAuditRow[];
};

export type TFeatureInstanceAuditGridHandle = {
  setQuickFilterText: (text: string) => void;
};

const FeatureInstanceAuditGrid = forwardRef<
  TFeatureInstanceAuditGridHandle,
  TFeatureInstanceAuditGridProps
>(function FeatureInstanceAuditGrid(props, ref) {
  const { loading, filteredRows } = props;
  const gridRef = useRef<AgGridReact<TEntityAuditMasterRow>>(null);

  useImperativeHandle(ref, () => ({
    setQuickFilterText: (text: string) => {
      gridRef.current?.api?.setGridOption("quickFilterText", text);
    }
  }));

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      flex: 1,
      minWidth: 180
    }),
    []
  );

  const masterColumnDefs = useMemo<ColDef<TEntityAuditMasterRow>[]>(() => {
    return [
      {
        colId: "groupTitle",
        field: "groupTitle",
        headerName: "Title",
        cellRenderer: "agGroupCellRenderer",
        flex: 1,
        minWidth: 220
      },
      {
        colId: "createdBy",
        field: "createdBy",
        headerName: "Created By",
        flex: 0,
        width: 180,
        minWidth: 160
      },
      {
        colId: "createdAt",
        field: "createdAt",
        headerName: "Created At",
        valueGetter: ({ data }) =>
          data?.createdAt ? moment(data.createdAt).format("MM-DD-YYYY") : "",
        flex: 0,
        width: 150,
        minWidth: 140
      },
      {
        colId: "updatedBy",
        field: "updatedBy",
        headerName: "Updated By",
        flex: 0,
        width: 180,
        minWidth: 160
      },
      {
        colId: "updatedAt",
        field: "updatedAt",
        headerName: "Updated At",
        sort: "asc",
        valueGetter: ({ data }) =>
          data?.updatedAt ? moment(data.updatedAt).format("MM-DD-YYYY") : "",
        flex: 0,
        width: 150,
        minWidth: 140
      }
    ];
  }, []);

  useEffect(() => {
    gridRef.current?.api?.setGridOption("loading", loading);
  }, [loading]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }

    api.applyColumnState({
      state: [{ colId: "updatedAt", sort: "asc" }],
      defaultState: { sort: null }
    });
  }, []);

  const masterRows = useMemo<TEntityAuditMasterRow[]>(() => {
    const rows = groupRecordsByTitle(filteredRows);
    rows.forEach(applyAggregatesToRow);
    return rows;
  }, [filteredRows]);

  return (
    <AgGridReact<TEntityAuditMasterRow>
      ref={gridRef}
      rowData={masterRows}
      columnDefs={masterColumnDefs}
      defaultColDef={defaultColDef}
      suppressContextMenu
      masterDetail
      isRowMaster={(dataItem) => (dataItem?.records?.length || 0) > 0}
      detailCellRenderer={EntityTimelineDetailRenderer}
      detailRowHeight={380}
      getRowId={(params) => params.data.groupKey}
      animateRows
    />
  );
});

export default FeatureInstanceAuditGrid;
